import type { CurrentObject, CurrentObjects } from "@loanlink-nl/model-app";
import { storeToRefs } from "pinia";
import { useDossierDataStore } from "@/store/useDossierDataStore";
import { computed } from "vue";

export function useCurrentObjects() {
	const dossierDataStore = useDossierDataStore();
	const { data: dossierData } = storeToRefs(dossierDataStore);

	function ensureCurrentObjects(): CurrentObjects {
		if (!dossierData.value.currentObjects) {
			dossierData.value.currentObjects = {
				hasOwnedProperty: undefined,
				objects: [],
			};
		}

		return dossierData.value.currentObjects;
	}

	const data = computed<CurrentObjects>({
		get: () => ensureCurrentObjects(),
		set: (value) => {
			dossierData.value.currentObjects = value;
		},
	});

	const hasOwnedProperty = computed<boolean | undefined>({
		get: () => data.value.hasOwnedProperty,
		set: (value) => {
			data.value.hasOwnedProperty = value;
			if (value === true && data.value.objects.length === 0) {
				createObject();
			}
			if (value === false) {
				data.value.objects = [];
			}
		},
	});

	const objects = computed(() => data.value.objects);

	const primaryObject = computed<CurrentObject | undefined>({
		get: () => data.value.objects[0],
		set: (value) => {
			if (value && data.value.objects.length > 0) {
				data.value.objects[0] = value;
			}
		},
	});

	function createObject(initialData?: Partial<CurrentObject>): CurrentObject {
		const newObject: CurrentObject = {
			id: crypto.randomUUID(),
			ownerOccupied: true,
			recreationalUse: false,
			...initialData,
		};
		data.value.objects.push(newObject);
		return newObject;
	}

	function getObject(id: string): CurrentObject | undefined {
		return data.value.objects.find((obj) => obj.id === id);
	}

	function updateObject(id: string, updates: Partial<CurrentObject>): void {
		const index = data.value.objects.findIndex((obj) => obj.id === id);
		if (index === -1) return;
		data.value.objects[index] = { ...data.value.objects[index]!, ...updates };
	}

	function deleteObject(id: string): void {
		const index = data.value.objects.findIndex((obj) => obj.id === id);
		if (index !== -1) {
			data.value.objects.splice(index, 1);
		}
	}

	return {
		data,
		hasOwnedProperty,
		objects,
		primaryObject,
		createObject,
		getObject,
		updateObject,
		deleteObject,
	};
}

export function useOwnerInference() {
	const dossierDataStore = useDossierDataStore();
	const { data: dossierData } = storeToRefs(dossierDataStore);

	function syncOwnerForObject(objectId: string): void {
		const currentObjects = dossierData.value.currentObjects;
		const existingMortgages = dossierData.value.existingMortgages;

		if (!currentObjects || !existingMortgages) return;

		const object = currentObjects.objects.find((o) => o.id === objectId);
		if (!object) return;

		const linkedMortgages = existingMortgages.filter(
			(m) => m.currentObjectId === objectId,
		);

		if (linkedMortgages.length === 0) return;

		if (object.owner) {
			for (const mortgage of linkedMortgages) {
				mortgage.owner = object.owner;
			}
		} else {
			const mortgageWithOwner = linkedMortgages.find((m) => m.owner);
			if (mortgageWithOwner) {
				object.owner = mortgageWithOwner.owner;
				for (const mortgage of linkedMortgages) {
					mortgage.owner = object.owner;
				}
			}
		}
	}

	function linkMortgageToObject(mortgageId: string, objectId: string): void {
		const mortgage = dossierData.value.existingMortgages?.find(
			(m) => m.id === mortgageId,
		);
		if (mortgage) {
			mortgage.currentObjectId = objectId;
			syncOwnerForObject(objectId);
		}
	}

	function unlinkMortgageFromObject(mortgageId: string): void {
		const mortgage = dossierData.value.existingMortgages?.find(
			(m) => m.id === mortgageId,
		);
		if (mortgage) {
			mortgage.currentObjectId = undefined;
		}
	}

	function getMortgagesForObject(objectId: string) {
		return (
			dossierData.value.existingMortgages?.filter(
				(m) => m.currentObjectId === objectId,
			) ?? []
		);
	}

	function getUnlinkedMortgages() {
		return (
			dossierData.value.existingMortgages?.filter((m) => !m.currentObjectId) ??
			[]
		);
	}

	return {
		syncOwnerForObject,
		linkMortgageToObject,
		unlinkMortgageFromObject,
		getMortgagesForObject,
		getUnlinkedMortgages,
	};
}
